// TEMPORAIRE — diagnostic d'upload, posé le 28/09/2026. À RETIRER après la collecte.
//
// Pourquoi : des membres ne parviennent plus à publier, et l'erreur affichée parle de leur
// connexion. Les premiers relevés montrent que ce n'en est pas une : dans la majorité des cas
// aucun octet ne quitte l'appareil et le fichier choisi n'est plus lisible localement. Reste à
// savoir POURQUOI il ne l'est plus — le droit d'accès accordé par Android a expiré, ou le média
// n'est pas réellement sur le téléphone (miniature affichée par la galerie, contenu resté en
// ligne). Le correctif n'est pas le même selon le cas, d'où ce module.
//
// Principe : on ne résume rien, on enregistre tout ce que le navigateur accepte de dire, à deux
// instants — quand le membre choisit son fichier, et quand l'envoi échoue. C'est la comparaison
// des deux qui tranche.
//
// Périmètre : publication d'un média, et UNIQUEMENT les erreurs réseau. Un 413 ou un 415 est déjà
// journalisé côté API, le renvoyer ferait doublon.

import { reportUploadFailure } from '../api/public-voissa/medias';

// Les seuls codes où la requête n'aboutit pas : c'est là qu'on est aveugles.
// ERR_UPLOAD_STALLED : envoi arrêté par le contrôle de coupure (useUploader), 30 s sans octet.
// ERR_UPLOAD_TOO_SLOW : même arrêt, la connexion étant déjà jugée trop lente (useUploader).
const CODES_RESEAU = ['ERR_NETWORK', 'ECONNABORTED', 'ERR_UPLOAD_STALLED', 'ERR_UPLOAD_TOO_SLOW'];

// Au-delà, une lecture qui n'a pas abouti ne le fera pas : on note l'abandon, qui est lui-même
// une information (un fichier local répond en quelques millisecondes).
const DELAI_LECTURE_MS = 10000;

/* -------------------------------------------------------------------------- */
/* Journal des événements du navigateur                                        */
/* -------------------------------------------------------------------------- */

// Android peut geler ou recycler un onglet passé en arrière-plan, et c'est l'un des moyens connus
// de perdre l'accès à un fichier déjà choisi. On date donc ces événements pour pouvoir dire, au
// moment de l'échec, ce qui s'est produit DEPUIS la sélection.
const evenements = [];

const noter = (type, detail) => {
    evenements.push({ t: Date.now(), type, ...(detail ? { detail } : {}) });
    if (evenements.length > 100) evenements.shift();
};

if (typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('visibilitychange', () => noter('visibilite', document.visibilityState));
    // Cycle de vie des pages : Chrome Android gèle un onglet en arrière-plan avant de le décharger.
    document.addEventListener('freeze', () => noter('gel'));
    document.addEventListener('resume', () => noter('reprise'));
}
if (typeof window !== 'undefined' && window.addEventListener) {
    window.addEventListener('online', () => noter('retourEnLigne'));
    window.addEventListener('offline', () => noter('perteReseau'));
    window.addEventListener('pagehide', (e) => noter('pageMasquee', e.persisted ? 'mise en cache' : 'dechargee'));
    window.addEventListener('pageshow', (e) => noter('pageAffichee', e.persisted ? 'restauree du cache' : 'chargee'));
}

/* -------------------------------------------------------------------------- */
/* Outils de lecture                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Décrit une exception sans rien perdre. `name` est l'information capitale : le navigateur y
 * distingue `NotFoundError` (le fichier n'est plus à l'emplacement référencé) de
 * `NotReadableError` (référence valide mais lecture impossible : droit perdu, ou contenu absent
 * de l'appareil) et de `SecurityError` (accès refusé).
 */
const decrireErreur = (e) => {
    if (!e) return { nom: 'inconnue', message: 'aucune erreur fournie par le navigateur' };
    return {
        nom: e.name || 'sansNom',
        message: e.message || String(e),
        code: e.code ?? null,
    };
};

const enHexa = (buffer) => Array.from(new Uint8Array(buffer || []))
    .map((o) => `0${o.toString(16)}`.slice(-2).toUpperCase())
    .join(' ');

/**
 * Lit une tranche du fichier et rapporte tout : durée, octets réellement obtenus, et l'erreur
 * exacte le cas échéant. La durée n'est pas un détail — un fichier présent sur l'appareil se lit
 * à plusieurs centaines de Mo/s, un fichier rapatrié depuis le cloud à la vitesse du réseau.
 * C'est ce qui sépare « droit perdu » de « média pas sur le téléphone ».
 */
const lireTranche = (file, debut, fin) => new Promise((resolve) => {
    const depart = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    let termine = false;
    let lecteur = null;

    const rendre = (resultat) => {
        if (termine) return;
        termine = true;
        const duree = Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - depart);
        resolve({ ...resultat, dureeMs: duree });
    };

    const minuteur = setTimeout(() => {
        try { lecteur?.abort(); } catch (e) { /* rien à faire */ }
        rendre({ ok: false, erreur: { nom: 'DelaiDepasse', message: `lecture non terminée en ${DELAI_LECTURE_MS} ms` } });
    }, DELAI_LECTURE_MS);

    try {
        lecteur = new FileReader();
        lecteur.onload = () => {
            clearTimeout(minuteur);
            rendre({ ok: true, octetsLus: lecteur.result ? lecteur.result.byteLength : 0, donnees: lecteur.result });
        };
        lecteur.onerror = () => {
            clearTimeout(minuteur);
            rendre({ ok: false, erreur: decrireErreur(lecteur.error) });
        };
        lecteur.onabort = () => {
            clearTimeout(minuteur);
            rendre({ ok: false, erreur: { nom: 'LectureInterrompue', message: 'lecture interrompue' } });
        };
        lecteur.readAsArrayBuffer(file.slice(debut, fin));
    } catch (e) {
        clearTimeout(minuteur);
        rendre({ ok: false, erreur: decrireErreur(e) });
    }
});

const debitMoParSeconde = (octets, dureeMs) => (
    dureeMs > 0 ? Number(((octets / 1048576) / (dureeMs / 1000)).toFixed(2)) : null
);

/**
 * Sonde l'accès au fichier par paliers croissants. Le premier palier qui échoue donne la cause ;
 * ceux qui réussissent donnent la vitesse de lecture, donc l'emplacement réel du contenu.
 *
 * @param {File} file
 * @param {Array} paliers  tranches à lire, de la plus petite à la plus grande
 * @returns {Promise<Object>} un compte rendu par palier + les octets d'en-tête s'ils ont pu être lus
 */
const sonderAcces = async (file, paliers) => {
    const comptes = [];
    let enTete = null;
    let stop = false;

    for (const palier of paliers) {
        if (stop) {
            comptes.push({ palier: palier.nom, tente: false, raison: 'palier précédent en échec' });
            continue;
        }
        // Un palier vise soit le début du fichier, soit sa toute fin.
        const debut = palier.depuisLaFin ? Math.max(0, (file.size || 0) - palier.depuisLaFin) : 0;
        const fin = palier.depuisLaFin
            ? (file.size || 0)
            // Inutile de demander plus que ce que le fichier annonce.
            : Math.min(palier.fin, file.size || palier.fin);
        const demandes = fin - debut;
        const r = await lireTranche(file, debut, fin);

        comptes.push({
            palier: palier.nom,
            tente: true,
            ok: r.ok,
            octetsDemandes: demandes,
            octetsLus: r.ok ? r.octetsLus : 0,
            dureeMs: r.dureeMs,
            debitMoSec: r.ok ? debitMoParSeconde(r.octetsLus, r.dureeMs) : null,
            // Une tranche qui rend moins d'octets que demandé trahit un fichier plus court que
            // ce qu'il annonce : les premières lectures passent, l'envoi complet casse.
            tailleCoherente: r.ok ? (r.octetsLus === demandes) : null,
            erreur: r.ok ? null : r.erreur,
        });

        if (!r.ok) stop = true;
        // Les premiers octets donnent le format réel du fichier, quelle que soit son extension.
        else if (palier.enTete && r.donnees) enTete = enHexa(r.donnees);
    }

    return { paliers: comptes, enTete };
};

// À la sélection, on reste léger : le membre attend son aperçu, on ne lit que de quoi établir que
// le fichier est accessible et connaître son format.
const PALIERS_SELECTION = [
    { nom: '1 octet', fin: 1 },
    { nom: '256 octets', fin: 256, enTete: true },
];

// Après l'échec, on va jusqu'au bout : 1 Mo lu (ou le fichier entier s'il est plus petit) révèle
// la vitesse réelle d'accès au contenu.
const PALIERS_ECHEC = [
    { nom: '1 octet', fin: 1 },
    { nom: '256 octets', fin: 256, enTete: true },
    { nom: '64 Ko', fin: 65536 },
    { nom: '1 Mo', fin: 1048576 },
    // Le test décisif : si la taille annoncée par le système ne correspond pas aux octets
    // réellement présents, le début du fichier se lit sans problème et c'est la fin qui manque
    // — exactement ce que l'envoi complet rencontrerait.
    { nom: 'fin du fichier', depuisLaFin: 65536 },
];

/* -------------------------------------------------------------------------- */
/* Marquage à la sélection                                                     */
/* -------------------------------------------------------------------------- */

// Nombre de tentatives d'envoi par fichier : quatre échecs d'affilée sur la même image ne se
// lisent pas comme un échec isolé.
const tentatives = new Map();
const cleFichier = (file) => `${file?.name}|${file?.size}|${file?.lastModified}`;

/**
 * À appeler au moment où le membre choisit son fichier. Date la sélection et vérifie
 * immédiatement que le fichier est lisible — c'est la mesure de référence : si elle réussit et
 * que la même lecture échoue à l'envoi, l'accès a été perdu ENTRE les deux, et rien d'autre ne
 * peut l'expliquer.
 *
 * Ne bloque pas et n'échoue jamais : le résultat est simplement déposé sur l'objet fichier.
 *
 * @param {File} file
 */
export const marquerSelection = (file) => {
    try {
        if (!file) return;
        file.__diagSelectionAt = Date.now();
        file.__diagSelection = { etat: 'en cours' };
        sonderAcces(file, PALIERS_SELECTION)
            .then((r) => { file.__diagSelection = { etat: 'terminee', ...r }; })
            .catch((e) => { file.__diagSelection = { etat: 'erreur', erreur: decrireErreur(e) }; });
    } catch (e) {
        // Silence volontaire : instrumenter ne doit jamais gêner la sélection.
    }
};

/* -------------------------------------------------------------------------- */
/* Contexte                                                                    */
/* -------------------------------------------------------------------------- */

const decrireConnexion = () => {
    const c = (typeof navigator !== 'undefined' && (navigator.connection || navigator.mozConnection || navigator.webkitConnection)) || {};
    return {
        effectiveType: c.effectiveType ?? null,
        type: c.type ?? null,
        debitMbps: c.downlink ?? null,
        debitMaxMbps: c.downlinkMax ?? null,
        latenceMs: c.rtt ?? null,
        economiseurDonnees: c.saveData ?? null,
    };
};

// Chrome Android réduit son user-agent (« Android 10; K ») : la version réelle du système et le
// modèle ne sont accessibles que par cette API, et seulement en la demandant explicitement.
const decrireNavigateur = async () => {
    const base = {
        userAgent: navigator.userAgent,
        plateforme: navigator.platform ?? null,
        langue: navigator.language ?? null,
        memoireAppareilGo: navigator.deviceMemory ?? null,
        coeurs: navigator.hardwareConcurrency ?? null,
        enLigne: navigator.onLine ?? null,
        // `window.screen` et non `screen` : le global nu est interdit par la configuration ESLint du projet.
        ecran: window.screen ? `${window.screen.width}x${window.screen.height}@${window.devicePixelRatio || 1}` : null,
    };
    try {
        if (navigator.userAgentData?.getHighEntropyValues) {
            const d = await navigator.userAgentData.getHighEntropyValues([
                'platformVersion', 'model', 'fullVersionList', 'architecture',
            ]);
            base.mobile = navigator.userAgentData.mobile ?? null;
            base.versionSysteme = d.platformVersion ?? null;
            base.modele = d.model ?? null;
            base.versions = (d.fullVersionList || []).map((v) => `${v.brand} ${v.version}`).join(', ') || null;
        }
    } catch (e) {
        base.detailsNavigateur = decrireErreur(e);
    }
    try {
        if (navigator.storage?.estimate) {
            const s = await navigator.storage.estimate();
            base.stockage = {
                quotaMo: s.quota ? Math.round(s.quota / 1048576) : null,
                utiliseMo: s.usage ? Math.round(s.usage / 1048576) : null,
            };
        }
    } catch (e) {
        // Sans importance : l'estimation de stockage n'est pas partout disponible.
    }
    return base;
};

// L'objet XHR sous-jacent dit si une réponse a été reçue. `status: 0` confirme qu'il n'y en a
// jamais eu — la requête n'est pas partie ou a été coupée avant toute réponse.
const decrireRequete = (error) => {
    const r = error?.request;
    if (!r) return null;
    return {
        readyState: r.readyState ?? null,
        statut: r.status ?? null,
        texteStatut: r.statusText || null,
        urlFinale: r.responseURL || null,
    };
};

/* -------------------------------------------------------------------------- */
/* Signalement                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Signale un envoi qui n'a pas abouti. Ne renvoie jamais d'erreur : un diagnostic qui échoue ne
 * doit surtout pas en déclencher un second, ni gêner le membre.
 *
 * @param {Object} params
 * @param {File} params.file fichier que le membre tentait d'envoyer
 * @param {*} params.error erreur axios remontée par l'envoi
 * @param {Object} [params.status] avancement au moment de l'échec
 * @param {number} [params.startedAt] horodatage du début de l'envoi
 * @param {string} [params.uploadPath] endpoint visé — distingue une publication d'un avatar
 */
export const signalerEchecUpload = async ({ file, error, status = {}, startedAt, uploadPath = null, relanceAvecCopie = false, relanceApresEnvoiComplet = false }) => {
    try {
        const code = error?.code;
        if (!file || !CODES_RESEAU.includes(code)) return;
        // Une copie locale (libs/fileCopy.js) n'a pas été « sélectionnée » : les mesures de la
        // sélection sont restées sur le fichier d'origine.
        const origine = file.__copiedFrom || file;

        const instantEchec = Date.now();
        const cle = cleFichier(file);
        const numeroTentative = (tentatives.get(cle) || 0) + 1;
        tentatives.set(cle, numeroTentative);

        const selectionAt = origine.__diagSelectionAt || null;
        // Ce qui s'est passé entre le choix du fichier et l'échec : un gel ou un passage en
        // arrière-plan survenu dans cet intervalle expliquerait une perte de droit.
        const depuisSelection = evenements
            .filter((e) => !selectionAt || e.t >= selectionAt)
            .map((e) => ({ ...e, apresSelectionMs: selectionAt ? e.t - selectionAt : null }));

        const [acces, navigateur] = await Promise.all([
            sonderAcces(file, PALIERS_ECHEC),
            decrireNavigateur(),
        ]);

        const rapport = {
            fichier: {
                nom: file.name,
                type: file.type || '(aucun type)',
                taille: file.size,
                modifieLe: file.lastModified || null,
                cheminRelatif: file.webkitRelativePath || null,
                // En-tête relu après l'échec : donne le format réel quand la lecture est possible.
                octets: acces.enTete,
                formatReel: identifierFormat(acces.enTete),
                // Copie en mémoire envoyée à la place du fichier d'origine (libs/fileCopy.js).
                copieLocale: !!file.__isLocalCopy,
            },
            lecture: {
                // Mesure de référence, prise au moment où le membre a choisi son fichier.
                aLaSelection: origine.__diagSelection || null,
                // Même mesure, refaite au moment de l'échec. La comparaison est le cœur du
                // diagnostic : lisible avant et plus après = accès perdu dans l'intervalle.
                apresEchec: acces.paliers,
                delaiSelectionEchecMs: selectionAt ? instantEchec - selectionAt : null,
            },
            echec: {
                code,
                nom: error?.name || null,
                message: error?.message,
                chemin: uploadPath,
                tentative: numeroTentative,
                // Échec avant le premier octet : l'envoi est relancé tout seul avec une copie.
                relanceAvecCopie,
                // Photo envoyée à 100 % puis coupée : un nouvel essai part tout seul.
                relanceApresEnvoiComplet,
                pourcentEnvoye: status?.percentCompleted ?? null,
                octetsEnvoyes: status?.loaded ?? null,
                dureeMs: startedAt ? instantEchec - startedAt : null,
                delaiSelectionEnvoiMs: (selectionAt && startedAt) ? startedAt - selectionAt : null,
                requete: decrireRequete(error),
                configuration: error?.config ? {
                    url: error.config.url,
                    methode: error.config.method,
                    delaiMaxMs: error.config.timeout,
                    avecIdentifiants: error.config.withCredentials,
                } : null,
                pile: error?.stack || null,
            },
            contexte: {
                connexion: decrireConnexion(),
                navigateur,
                pageVisible: document.visibilityState === 'visible',
                pageActive: typeof document.hasFocus === 'function' ? document.hasFocus() : null,
                evenementsDepuisSelection: depuisSelection,
                horodatages: {
                    selection: selectionAt,
                    debutEnvoi: startedAt || null,
                    echec: instantEchec,
                },
            },
        };

        await reportUploadFailure(rapport);
    } catch (e) {
        // Silence volontaire : ce module ne doit jamais faire de bruit chez le membre.
    }
};

/* -------------------------------------------------------------------------- */
/* Aperçu indisponible                                                         */
/* -------------------------------------------------------------------------- */

// Un aperçu qui ne s'affiche pas peut avoir deux causes très différentes : le fichier n'est plus
// lisible (même perte d'accès que pour l'envoi), ou le navigateur ne sait pas décoder ce que
// contient le fichier (codec exotique). Rien ne les distingue à l'écran — dans les deux cas le
// membre voit un rectangle gris — d'où ce second signalement.

// Au-delà, un aperçu qui n'a rien affiché ne le fera pas : on le constate sans attendre
// davantage.
const DELAI_APERCU_MS = 8000;

// Le code d'erreur d'un élément média, traduit. C'est lui qui sépare l'accès du décodage.
const CAUSES_MEDIA = {
    1: 'chargement interrompu',
    2: 'erreur réseau pendant le chargement',
    3: 'décodage impossible (contenu illisible ou codec non géré)',
    4: 'source non prise en charge (format ou accès refusé)',
};


// Un MP4 n'est pas un format mais un conteneur : ce que le navigateur sait ou non décoder, c'est
// le codec qu'il y a dedans. Un téléphone réglé en « haute efficacité » filme en H.265, que
// Chrome sur Android ne décode pas — le fichier s'envoie sans problème mais l'aperçu reste vide.
// D'où la question à laquelle ces deux relevés répondent : le fichier contient quoi, et le
// navigateur sait lire quoi ?

// Les identifiants à quatre lettres qui nomment les pistes dans un conteneur MP4 ou MOV.
const CODECS_CONNUS = ['avc1', 'avc3', 'hvc1', 'hev1', 'av01', 'vp09', 'vp08', 'mp4v',
                       'mp4a', 'ac-3', 'ec-3', 'Opus', 'alac', 'sowt'];

// Ce qu'on demande au navigateur de confirmer, indépendamment du fichier.
const CANDIDATS = {
    'H.264': 'video/mp4; codecs="avc1.42E01E"',
    'H.265 (HEVC)': 'video/mp4; codecs="hvc1.1.6.L93.B0"',
    'AV1': 'video/mp4; codecs="av01.0.05M.08"',
    'VP9': 'video/mp4; codecs="vp09.00.10.08"',
    'AAC': 'audio/mp4; codecs="mp4a.40.2"',
};


/**
 * Identifie le format réel d'après les premiers octets, sans se fier à l'extension ni au type
 * annoncé — les deux mentent régulièrement (une photo d'iPhone arrive parfois nommée `.jpg`
 * alors que c'est du HEIC). C'est souvent l'explication d'un aperçu vide : le fichier est
 * parfaitement lisible, mais le navigateur ne sait pas l'afficher.
 *
 * @param {string} hex les premiers octets en hexadécimal, tels que relevés par sonderAcces
 */
const identifierFormat = (hex) => {
    if (!hex) return null;
    const o = hex.split(' ').filter(Boolean).map((h) => parseInt(h, 16));
    const txt = (debut, longueur) => o.slice(debut, debut + longueur)
        .map((c) => String.fromCharCode(c)).join('');
    const debutePar = (...codes) => codes.every((c, i) => o[i] === c);
    const format = (nom, affichable) => ({ nom, affichable });

    if (debutePar(0xFF, 0xD8, 0xFF)) return format('JPEG', true);
    if (debutePar(0x89, 0x50, 0x4E, 0x47)) return format('PNG', true);
    if (txt(0, 4) === 'GIF8') return format('GIF', true);
    if (txt(0, 4) === 'RIFF' && txt(8, 4) === 'WEBP') return format('WebP', true);
    if (txt(0, 4) === 'RIFF' && txt(8, 4) === 'AVI ') return format('AVI', false);
    if (debutePar(0xFF, 0x0A) || txt(4, 4) === 'JXL ') return format('JPEG XL', false);
    if (debutePar(0x42, 0x4D)) return format('BMP', true);
    if (debutePar(0x1A, 0x45, 0xDF, 0xA3)) return format('Matroska (MKV ou WebM)', 'selon le codec');
    if (debutePar(0x49, 0x49, 0x2A, 0x00) || debutePar(0x4D, 0x4D, 0x00, 0x2A)) return format('TIFF ou DNG', false);

    // Famille ISO-BMFF : la marque qui suit `ftyp` distingue une photo d'iPhone d'une vidéo.
    if (txt(4, 4) === 'ftyp') {
        const marque = txt(8, 4);
        if (['heic', 'heix', 'heim', 'heis', 'hevc', 'hevx', 'mif1', 'msf1'].includes(marque)) {
            return format(`HEIC/HEIF (${marque})`, false);
        }
        if (['avif', 'avis'].includes(marque)) return format(`AVIF (${marque})`, true);
        if (marque.startsWith('qt')) return format('QuickTime MOV', 'selon le codec');
        if (marque.startsWith('crx')) return format('Canon CR3', false);
        return format(`MP4 (${marque})`, 'selon le codec');
    }
    return format('non identifié', null);
};

/**
 * Lit les codes de format déclarés par le fichier lui-même. Dans un MP4 ou un MOV, la table
 * `stsd` décrit chaque piste ; les quatre octets qui suivent l'en-tête de sa première entrée
 * donnent le code du format — et ce, quel que soit ce code. C'est ce qui permet de nommer un
 * codec auquel on n'avait pas pensé, au lieu de ne rien rapporter du tout.
 */
const codesDeclares = (octets) => {
    const codes = new Set();
    for (let i = 0; i + 20 <= octets.length; i++) {
        // 's' 't' 's' 'd'
        if (octets[i] === 0x73 && octets[i + 1] === 0x74 && octets[i + 2] === 0x73 && octets[i + 3] === 0x64) {
            let code = '';
            for (let j = i + 16; j < i + 20; j++) code += String.fromCharCode(octets[j]);
            // Un code de format est fait de quatre caractères imprimables.
            if (/^[A-Za-z0-9 ._-]{4}$/.test(code)) codes.add(code);
        }
    }
    return [...codes];
};

/**
 * Cherche les codecs présents dans le fichier. La table des pistes se trouve au début des
 * fichiers préparés pour la lecture en ligne, mais à la fin de ceux que produisent les appareils
 * photo : on regarde des deux côtés.
 *
 * @returns {Promise<Object>} les codes trouvés, ceux qu'on ne connaissait pas, et le cas échéant
 *                            la raison pour laquelle on n'a rien pu lire — un résultat vide ne
 *                            doit jamais rester ambigu.
 */
const chercherCodecs = async (file) => {
    const trouves = new Set();
    let luAuMoinsUneFois = false;
    const tranches = [
        [0, Math.min(65536, file.size)],
        [Math.max(0, file.size - 65536), file.size],
    ];
    for (const [debut, fin] of tranches) {
        if (fin <= debut) continue;
        const r = await lireTranche(file, debut, fin);
        if (!r.ok || !r.donnees) continue;
        luAuMoinsUneFois = true;
        const octets = new Uint8Array(r.donnees);
        // Ce que le fichier déclare lui-même, sans liste préétablie.
        codesDeclares(octets).forEach((code) => trouves.add(code));
        // Filet : certains conteneurs nomment le codec ailleurs que dans `stsd`.
        // Par blocs : passer 65 536 arguments d'un coup dépasse la limite du moteur.
        let texte = '';
        for (let i = 0; i < octets.length; i += 8192) {
            texte += String.fromCharCode.apply(null, octets.subarray(i, i + 8192));
        }
        CODECS_CONNUS.forEach((code) => { if (texte.indexOf(code) !== -1) trouves.add(code); });
    }

    const liste = [...trouves];
    return {
        trouves: liste,
        // Mis en avant : c'est précisément ce qu'on ne savait pas chercher.
        inconnus: liste.filter((code) => !CODECS_CONNUS.includes(code)),
        remarque: liste.length ? null
            : (luAuMoinsUneFois
                ? 'aucun code de format dans les 64 Ko de début et de fin'
                : 'fichier illisible, codecs non déterminables'),
    };
};

const capacitesDuNavigateur = (element) => {
    if (typeof element.canPlayType !== 'function') return null;
    const r = {};
    Object.entries(CANDIDATS).forEach(([nom, type]) => { r[nom] = element.canPlayType(type) || 'non géré'; });
    return r;
};

const signalerApercuIndisponible = async ({ file, element, cause, delaiMs }) => {
    try {
        if (!file || !element) return;

        const estVideo = element.tagName === 'VIDEO';
        const erreur = element.error || null;

        const [acces, navigateur] = await Promise.all([
            sonderAcces(file, PALIERS_ECHEC),
            decrireNavigateur(),
        ]);
        // Inutile sur une image, et impossible si le fichier n'est plus lisible.
        const codecs = estVideo ? await chercherCodecs(file) : null;

        const rapport = {
            evenement: 'apercu',
            fichier: {
                nom: file.name,
                type: file.type || '(aucun type)',
                taille: file.size,
                modifieLe: file.lastModified || null,
                octets: acces.enTete,
                // Ce que le fichier est vraiment, par-delà son extension et son type annoncé.
                formatReel: identifierFormat(acces.enTete),
            },
            apercu: {
                cause,
                delaiMs,
                element: estVideo ? 'video' : 'image',
                codeErreur: erreur?.code ?? null,
                causeErreur: erreur?.code ? (CAUSES_MEDIA[erreur.code] || 'inconnue') : null,
                // Chrome détaille ici la raison réelle du refus (par exemple le démultiplexeur
                // qui n'ouvre pas le fichier) : c'est souvent la ligne la plus instructive.
                messageErreur: erreur?.message || null,
                networkState: element.networkState ?? null,
                readyState: element.readyState ?? null,
                largeur: element.videoWidth ?? element.naturalWidth ?? null,
                hauteur: element.videoHeight ?? element.naturalHeight ?? null,
                duree: Number.isFinite(element.duration) ? element.duration : null,
                // Ce que le navigateur dit savoir lire, avant même d'essayer.
                formatAnnonce: estVideo && typeof element.canPlayType === 'function'
                    ? (element.canPlayType(file.type) || '(non géré)')
                    : null,
                // Ce que le fichier contient réellement, face à ce que le navigateur sait lire.
                codecsDansLeFichier: codecs,
                codecsGeresIci: estVideo ? capacitesDuNavigateur(element) : null,
            },
            lecture: {
                aLaSelection: file.__diagSelection || null,
                // Le fichier est-il encore lisible au moment où l'aperçu renonce ? C'est ce qui
                // sépare une perte d'accès d'un problème de format.
                auConstat: acces.paliers,
                delaiSelectionConstatMs: file.__diagSelectionAt ? Date.now() - file.__diagSelectionAt : null,
            },
            contexte: {
                connexion: decrireConnexion(),
                navigateur,
                pageVisible: document.visibilityState === 'visible',
                pageActive: typeof document.hasFocus === 'function' ? document.hasFocus() : null,
                evenementsDepuisSelection: evenements
                    .filter((e) => !file.__diagSelectionAt || e.t >= file.__diagSelectionAt)
                    .map((e) => ({ ...e, apresSelectionMs: file.__diagSelectionAt ? e.t - file.__diagSelectionAt : null })),
                horodatages: { selection: file.__diagSelectionAt || null, constat: Date.now() },
            },
        };

        await reportUploadFailure(rapport);
    } catch (e) {
        // Silence volontaire : ce module ne doit jamais faire de bruit chez le membre.
    }
};

/**
 * Signale l'échec d'une copie locale (libs/fileCopy.js) : à la sélection d'une photo, ou à la
 * relance d'un envoi refusé par Chrome. Sans ce rapport, l'échec n'était visible que du membre :
 * le 12/10/2026, 7 vidéos d'un même membre (retouchées par la galerie Samsung) ont été perdues
 * après une relance avec copie, sans aucune trace côté serveur.
 *
 * @param {Object} params
 * @param {File} params.file fichier que l'on tentait de copier
 * @param {*} params.error FileCopyError (raison, cause, détails)
 * @param {string} params.moment 'selection' ou 'relance'
 * @param {string} [params.uploadPath] endpoint visé
 */
export const signalerEchecCopie = async ({ file, error, moment, uploadPath = null }) => {
    try {
        if (!file) return;
        const [acces, navigateur] = await Promise.all([
            sonderAcces(file, PALIERS_ECHEC),
            decrireNavigateur(),
        ]);
        await reportUploadFailure({
            evenement: 'copie',
            fichier: {
                nom: file.name,
                type: file.type || '(aucun type)',
                taille: file.size,
                modifieLe: file.lastModified || null,
                octets: acces.enTete,
                formatReel: identifierFormat(acces.enTete),
            },
            lecture: {
                aLaSelection: (file.__copiedFrom || file).__diagSelection || null,
                // Relue juste après l'échec de la copie : le fichier est-il encore lisible par
                // petits morceaux, alors que la copie complète a échoué ?
                apresEchec: acces.paliers,
            },
            copie: {
                moment,
                chemin: uploadPath,
                raison: error?.reason || null,
                ...(error?.details || {}),
                erreur: decrireErreur(error?.cause || error),
            },
            contexte: {
                connexion: decrireConnexion(),
                navigateur,
                pageVisible: document.visibilityState === 'visible',
                horodatages: { selection: (file.__copiedFrom || file).__diagSelectionAt || null, constat: Date.now() },
            },
        });
    } catch (e) {
        // Silence volontaire : ce module ne doit jamais faire de bruit chez le membre.
    }
};

/**
 * Signale l'échec d'une conversion HEIC/WEBP → JPEG (libs/imageConvert.js). Le membre a vu un
 * message à la sélection ; sans ce rapport, rien ne dirait côté serveur quel fichier a résisté.
 *
 * @param {Object} params
 * @param {File} params.file fichier d'origine
 * @param {*} params.error ImageConvertError (raison + cause d'origine)
 * @param {string} [params.format] format détecté (heic, webp)
 */
export const signalerEchecConversion = async ({ file, error, format = null }) => {
    try {
        if (!file) return;
        const [acces, navigateur] = await Promise.all([
            sonderAcces(file, PALIERS_ECHEC),
            decrireNavigateur(),
        ]);
        await reportUploadFailure({
            evenement: 'conversion',
            fichier: {
                nom: file.name,
                type: file.type || '(aucun type)',
                taille: file.size,
                modifieLe: file.lastModified || null,
                octets: acces.enTete,
                formatReel: identifierFormat(acces.enTete),
            },
            conversion: {
                format,
                raison: error?.reason || null,
                erreur: decrireErreur(error?.cause || error),
            },
            contexte: {
                connexion: decrireConnexion(),
                navigateur,
                horodatages: { selection: file.__diagSelectionAt || null, constat: Date.now() },
            },
        });
    } catch (e) {
        // Silence volontaire : ce module ne doit jamais faire de bruit chez le membre.
    }
};

/**
 * Surveille un élément d'aperçu et signale s'il n'affiche rien. Deux déclencheurs : l'erreur de
 * chargement, et l'absence de tout rendu au bout de quelques secondes — le rectangle gris
 * décrit par les membres ne s'accompagne pas toujours d'une erreur.
 *
 * Se branche en `ref` : `<video ref={(el) => surveillerApercu(el, file)} …>`.
 *
 * @param {HTMLElement} element élément d'aperçu (video ou img)
 * @param {File} file fichier affiché
 */
export const surveillerApercu = (element, file) => {
    try {
        if (!element || !file) return;
        // Le `ref` est rappelé à chaque rendu : on ne surveille qu'une fois par fichier.
        if (element.__diagApercu === file) return;
        // La copie locale d'une photo (libs/fileCopy.js) remplace le fichier sans changer l'aperçu,
        // déjà affiché : rien ne se rechargera, la surveiller la ferait passer pour un échec.
        if (file.__copiedFrom && element.__diagApercu === file.__copiedFrom) {
            element.__diagApercu = file;
            return;
        }
        element.__diagApercu = file;

        const depart = Date.now();
        let clos = false;
        let minuteur = null;

        const renoncer = (cause) => {
            if (clos) return;
            clos = true;
            clearTimeout(minuteur);
            signalerApercuIndisponible({ file, element, cause, delaiMs: Date.now() - depart });
        };
        const reussi = () => { clos = true; clearTimeout(minuteur); };

        element.addEventListener('error', () => renoncer("erreur de chargement"), { once: true });
        element.addEventListener('loadeddata', reussi, { once: true }); // vidéo
        element.addEventListener('load', reussi, { once: true });       // image
        minuteur = setTimeout(() => renoncer(`aucun rendu après ${DELAI_APERCU_MS / 1000} s`), DELAI_APERCU_MS);
    } catch (e) {
        // Silence volontaire.
    }
};

export default signalerEchecUpload;
